export interface ReadingTheme {
  id: string;
  name: string;
  icon: string;
  description: string;
  colors: {
    primary: string;
    primaryRgb: string;
    secondary: string;
    secondaryRgb: string;
    accent: string;
    accentRgb: string;
    background: string;
    backgroundRgb: string;
    surface: string;
    text: string;
    textMuted: string;
    heroOverlay: string;
  };
  atmosphere: {
    gradient: string;
    pattern: string;
    intensity: number;
    heroGradient: string;
    sectionGradient: string;
  };
  effects: {
    blur: number;
    contrast: number;
    brightness: number;
    heroParallax: number;
  };
}

export const readingThemes: Record<string, ReadingTheme> = {
  regenerative: {
    id: 'regenerative',
    name: 'Regenerative',
    icon: '🌱',
    description: 'Nature & growth reading environment',
    colors: {
      primary: '#047857',
      primaryRgb: '4, 120, 87',
      secondary: '#065F46',
      secondaryRgb: '6, 95, 70',
      accent: '#10B981',
      accentRgb: '16, 185, 129',
      background: '#F0FDF4',
      backgroundRgb: '240, 253, 244',
      surface: '#D1FAE5',
      text: '#064E3B',
      textMuted: '#047857',
      heroOverlay: 'from-emerald-900/80 via-green-800/60 to-teal-700/40',
    },
    atmosphere: {
      gradient:
        'radial-gradient(ellipse at top right, rgba(16, 185, 129, 0.3), rgba(6, 95, 70, 0.2), transparent)',
      pattern: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23047857' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      intensity: 0.9,
      heroGradient:
        'linear-gradient(135deg, rgba(4, 120, 87, 0.15), rgba(6, 95, 70, 0.1), rgba(16, 185, 129, 0.05))',
      sectionGradient:
        'linear-gradient(180deg, rgba(240, 253, 244, 0), rgba(209, 250, 229, 0.5), rgba(240, 253, 244, 0))',
    },
    effects: {
      blur: 0,
      contrast: 1.05,
      brightness: 1.02,
      heroParallax: 1.15,
    },
  },
  revolutionary: {
    id: 'revolutionary',
    name: 'Revolutionary',
    icon: '🔥',
    description: 'Disruption & energy reading mode',
    colors: {
      primary: '#DC2626',
      primaryRgb: '220, 38, 38',
      secondary: '#B91C1C',
      secondaryRgb: '185, 28, 28',
      accent: '#EF4444',
      accentRgb: '239, 68, 68',
      background: '#FEF2F2',
      backgroundRgb: '254, 242, 242',
      surface: '#FEE2E2',
      text: '#7F1D1D',
      textMuted: '#991B1B',
      heroOverlay: 'from-red-950/90 via-rose-900/70 to-pink-800/50',
    },
    atmosphere: {
      gradient:
        'radial-gradient(ellipse at bottom left, rgba(220, 38, 38, 0.2), rgba(185, 28, 28, 0.15), transparent)',
      pattern: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23DC2626' fill-opacity='0.02'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      intensity: 1.2,
      heroGradient:
        'linear-gradient(135deg, rgba(220, 38, 38, 0.2), rgba(185, 28, 28, 0.15), rgba(239, 68, 68, 0.1))',
      sectionGradient:
        'linear-gradient(180deg, rgba(254, 242, 242, 0), rgba(254, 226, 226, 0.4), rgba(254, 242, 242, 0))',
    },
    effects: {
      blur: 2,
      contrast: 1.1,
      brightness: 1.05,
      heroParallax: 1.2,
    },
  },
  innovative: {
    id: 'innovative',
    name: 'Innovative',
    icon: '💡',
    description: 'Technology & trust reading space',
    colors: {
      primary: '#2563EB',
      primaryRgb: '37, 99, 235',
      secondary: '#1E40AF',
      secondaryRgb: '30, 64, 175',
      accent: '#3B82F6',
      accentRgb: '59, 130, 246',
      background: '#EFF6FF',
      backgroundRgb: '239, 246, 255',
      surface: '#DBEAFE',
      text: '#1E3A8A',
      textMuted: '#1E40AF',
      heroOverlay: 'from-blue-950/80 via-indigo-800/60 to-sky-700/40',
    },
    atmosphere: {
      gradient:
        'linear-gradient(180deg, rgba(239, 246, 255, 0), rgba(219, 234, 254, 0.8), rgba(239, 246, 255, 0))',
      pattern: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563EB' fill-opacity='0.02'%3E%3Cpath d='M0 0h20v20H0V0zm40 0h20v20H40V0zM20 20h20v20H20V20zm0 20h20v20H20V40zm20 0h20v20H40V40z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      intensity: 0.8,
      heroGradient:
        'linear-gradient(135deg, rgba(37, 99, 235, 0.15), rgba(30, 64, 175, 0.1), rgba(59, 130, 246, 0.05))',
      sectionGradient:
        'linear-gradient(180deg, rgba(239, 246, 255, 0), rgba(219, 234, 254, 0.5), rgba(239, 246, 255, 0))',
    },
    effects: {
      blur: 0,
      contrast: 1.02,
      brightness: 1.01,
      heroParallax: 1.1,
    },
  },
  community: {
    id: 'community',
    name: 'Community',
    icon: '🤝',
    description: 'Connection & warmth for shared stories',
    colors: {
      primary: '#D97706',
      primaryRgb: '217, 119, 6',
      secondary: '#B45309',
      secondaryRgb: '180, 83, 9',
      accent: '#F59E0B',
      accentRgb: '245, 158, 11',
      background: '#FFFBEB',
      backgroundRgb: '255, 251, 235',
      surface: '#FEF3C7',
      text: '#78350F',
      textMuted: '#92400E',
      heroOverlay: 'from-amber-900/80 via-orange-800/60 to-yellow-700/40',
    },
    atmosphere: {
      gradient:
        'radial-gradient(ellipse at center, rgba(217, 119, 6, 0.25), rgba(245, 158, 11, 0.15), transparent)',
      pattern: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D97706' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3Ccircle cx='15' cy='15' r='2'/%3E%3Ccircle cx='45' cy='15' r='2'/%3E%3Ccircle cx='15' cy='45' r='2'/%3E%3Ccircle cx='45' cy='45' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      intensity: 1,
      heroGradient:
        'linear-gradient(135deg, rgba(217, 119, 6, 0.2), rgba(180, 83, 9, 0.15), rgba(245, 158, 11, 0.1))',
      sectionGradient:
        'linear-gradient(180deg, rgba(255, 251, 235, 0), rgba(254, 243, 199, 0.6), rgba(255, 251, 235, 0))',
    },
    effects: {
      blur: 1,
      contrast: 1.02,
      brightness: 1.01,
      heroParallax: 1.1,
    },
  },
};

// Helper to apply theme CSS variables
export const applyReadingTheme = (theme: ReadingTheme) => {
  const root = document.documentElement;

  // Apply color variables
  root.style.setProperty('--reading-theme-primary', theme.colors.primary);
  root.style.setProperty('--reading-theme-primary-rgb', theme.colors.primaryRgb);
  root.style.setProperty('--reading-theme-secondary', theme.colors.secondary);
  root.style.setProperty('--reading-theme-secondary-rgb', theme.colors.secondaryRgb);
  root.style.setProperty('--reading-theme-accent', theme.colors.accent);
  root.style.setProperty('--reading-theme-accent-rgb', theme.colors.accentRgb);
  root.style.setProperty('--reading-theme-background', theme.colors.background);
  root.style.setProperty('--reading-theme-background-rgb', theme.colors.backgroundRgb);
  root.style.setProperty('--reading-theme-surface', theme.colors.surface);
  root.style.setProperty('--reading-theme-text', theme.colors.text);
  root.style.setProperty('--reading-theme-text-muted', theme.colors.textMuted);

  // Apply atmosphere variables
  root.style.setProperty('--reading-atmosphere-gradient', theme.atmosphere.gradient);
  root.style.setProperty('--reading-atmosphere-pattern', theme.atmosphere.pattern);
  root.style.setProperty('--reading-atmosphere-intensity', theme.atmosphere.intensity.toString());
  root.style.setProperty('--reading-atmosphere-hero-gradient', theme.atmosphere.heroGradient);
  root.style.setProperty('--reading-atmosphere-section-gradient', theme.atmosphere.sectionGradient);

  // Apply effect variables
  root.style.setProperty('--reading-effects-blur', `${theme.effects.blur}px`);
  root.style.setProperty('--reading-effects-contrast', theme.effects.contrast.toString());
  root.style.setProperty('--reading-effects-brightness', theme.effects.brightness.toString());
  root.style.setProperty('--reading-effects-hero-parallax', theme.effects.heroParallax.toString());
};

// Get theme from session storage
export const getStoredTheme = (): string => {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem('reading-theme') || 'regenerative';
  }
  return 'regenerative';
};

// Store theme in session storage
export const storeTheme = (themeId: string) => {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('reading-theme', themeId);
  }
};
